import { useEffect, useRef, useState } from 'react';
import {
  clampSticky,
  STICKY_COLORS,
  STICKY_SIZE,
  type StickyNote,
} from '@td/shared';
import { useDeleteSticky, useStickyNotes, useUpdateSticky } from '../hooks/useSticky';
import { useConfirm } from './ConfirmDialog';

/**
 * Sticky notes, over the whole dashboard.
 *
 * The first version put them inside a card of their own, which missed the
 * point entirely: a sticky note goes *on top of the thing it is about. Drag
 * one over the weather and write "check this before the trip" and the note is
 * doing its job by being in the way. Penned into a panel on the left it was
 * just a small second notes page.
 *
 * So this is a layer rather than a card. It covers the board exactly — same
 * coordinates as the cards, so a note stays put relative to what it was stuck
 * to — and it lets every click through except on the notes themselves. That
 * last part is the whole trick: `pointer-events-none` on the layer and
 * `pointer-events-auto` on each note, so an invisible sheet over the
 * dashboard does not swallow every button underneath it.
 */
export function StickyLayer({ board }: { board: { width: number; height: number } }) {
  const { data: notes = [] } = useStickyNotes();
  const update = useUpdateSticky();
  const remove = useDeleteSticky();
  const confirm = useConfirm();

  const topZ = notes.reduce((highest, n) => Math.max(highest, n.z), 0);

  if (notes.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: STICKY_LAYER_Z }}>
      {notes.map((note) => (
        <Sticky
          key={note.id}
          note={note}
          board={board}
          onMove={(x, y) => update.mutate({ id: note.id, x, y, z: topZ + 1 })}
          onWrite={(text) => update.mutate({ id: note.id, text })}
          onColor={(color) => update.mutate({ id: note.id, color })}
          onDelete={async () => {
            const ok = await confirm({
              title: 'Throw this note away?',
              body: 'Sticky notes have no trash — this one goes for good.',
              confirmLabel: 'Throw it away',
              danger: true,
            });
            if (ok) remove.mutate(note.id);
          }}
        />
      ))}
    </div>
  );
}

/**
 * Above every card, always.
 *
 * The cards stack from 1 upwards, one per card, so a dozen of them never
 * reaches this. A note is a piece of paper laid on the desk; a card that
 * covered one would be a card that had swallowed a reminder.
 */
const STICKY_LAYER_Z = 40;

/** One square of paper. */
function Sticky({
  note,
  board,
  onMove,
  onWrite,
  onColor,
  onDelete,
}: {
  note: StickyNote;
  board: { width: number; height: number };
  onMove: (x: number, y: number) => void;
  onWrite: (text: string) => void;
  onColor: (color: string) => void;
  onDelete: () => void;
}) {
  const [text, setText] = useState(note.text);
  const [at, setAt] = useState({ x: note.x, y: note.y });
  const [picking, setPicking] = useState(false);

  // The saved copy wins whenever it changes underneath an untouched note —
  // another tab, or the same note open on the classroom machine.
  useEffect(() => setText(note.text), [note.text]);
  useEffect(() => setAt({ x: note.x, y: note.y }), [note.x, note.y]);

  /*
   * Saved a moment after typing stops.
   *
   * A note is four words; asking somebody to press Save on four words is the
   * friction that means the note never gets written. The delay is what keeps
   * that from being a request per keystroke.
   */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (text === note.text) return;

    timer.current = setTimeout(() => onWrite(text), 700);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  /*
   * Dragging, from the note's top strip only.
   *
   * The body of a sticky note is a text box, and a text box you cannot click
   * into without moving the note is a text box that cannot be used. So the
   * grab handle is the strip along the top, the way a window title bar works.
   *
   * The pointer is deliberately *not* captured, and that is the whole of why
   * the ✕ works. Capturing on pointerdown hands the entire gesture to the
   * capturing element — including the click at the end of it — so the strip
   * swallowed the clicks belonging to the two buttons sitting inside it, and
   * both looked completely dead. I had already made and fixed this exact
   * mistake on the jobs board and made it again here. Window listeners do the
   * same job with nothing to steal: they keep working when the pointer leaves
   * the note, which is the only thing capture was buying.
   */
  const from = useRef<{ x: number; y: number; noteX: number; noteY: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  function begin(e: React.PointerEvent) {
    if (e.button !== 0) return;

    // A press on a control inside the strip is a press, not a drag.
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;

    from.current = { x: e.clientX, y: e.clientY, noteX: at.x, noteY: at.y };
    setDragging(true);
  }

  /*
   * On the window rather than the note, so a fast drag that outruns the
   * pointer — or one that crosses a card — carries on rather than stopping
   * dead wherever the mouse left the strip.
   */
  useEffect(() => {
    if (!dragging) return;

    function move(e: PointerEvent) {
      const start = from.current;
      if (!start) return;

      setAt(
        clampSticky(
          { x: start.noteX + (e.clientX - start.x), y: start.noteY + (e.clientY - start.y) },
          board,
        ),
      );
    }

    function up() {
      from.current = null;
      setDragging(false);
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [dragging, board]);

  /*
   * Saved when the drag ends rather than during it — one request for a move,
   * not one per pixel.
   */
  useEffect(() => {
    if (dragging) return;
    if (at.x === note.x && at.y === note.y) return;

    onMove(at.x, at.y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  return (
    <div
      /*
       * `pointer-events-auto` puts this one square back in the way of the
       * mouse, on a layer that is otherwise transparent to it. Without the
       * pair, a sheet over the dashboard would eat every click on every card.
       */
      className={[
        'pointer-events-auto absolute rounded-lg',
        dragging ? 'shadow-lifted' : 'shadow-soft',
      ].join(' ')}
      style={{
        left: at.x,
        top: at.y,
        width: STICKY_SIZE.width,
        minHeight: STICKY_SIZE.height,
        backgroundColor: note.color,
        // Within the layer, the most recently moved note sits on top.
        zIndex: note.z,
        // A hint of paper: a square of colour with no edge reads as a div.
        border: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <div
        onPointerDown={begin}
        className="flex cursor-grab items-center justify-between gap-1 rounded-t-lg px-1.5 py-1 active:cursor-grabbing"
        style={{ backgroundColor: 'rgba(0,0,0,0.07)' }}
      >
        <span aria-hidden className="select-none text-[10px] tracking-tight text-black/40">
          ⠿
        </span>

        <span className="flex items-center gap-0.5" data-no-drag>
          <button
            type="button"
            aria-label="Change the colour"
            title="Change the colour"
            onClick={() => setPicking((p) => !p)}
            className="rounded p-0.5 transition-colors hover:bg-black/10"
          >
            <span className="block h-3 w-3 rounded-full border border-black/25 bg-white/70" />
          </button>

          <button
            type="button"
            aria-label="Throw this note away"
            title="Throw it away"
            onClick={onDelete}
            className="rounded px-1 text-xs leading-none text-black/50 transition-colors hover:bg-black/10 hover:text-black/80"
          >
            ✕
          </button>
        </span>
      </div>

      {picking && (
        <div className="flex flex-wrap gap-1 border-b border-black/10 px-1.5 py-1">
          {STICKY_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              aria-label={c.name}
              title={c.name}
              aria-pressed={note.color === c.value}
              onClick={() => {
                onColor(c.value);
                setPicking(false);
              }}
              className={[
                'h-4 w-4 rounded-full border transition-transform',
                note.color === c.value ? 'scale-110 border-black/60' : 'border-black/20',
              ].join(' ')}
              style={{ backgroundColor: c.value }}
            />
          ))}
        </div>
      )}

      {/*
        Text, never markup. A sticky note with formatting is a document in
        disguise, and rendering it as text is what makes it impossible for one
        to carry anything but writing.
      */}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text !== note.text) onWrite(text);
        }}
        maxLength={2000}
        aria-label="Note"
        placeholder="Write something…"
        className="w-full resize-none border-none bg-transparent px-2 py-1.5 text-sm leading-snug text-black/80 placeholder:text-black/35 focus:outline-none"
        style={{ height: STICKY_SIZE.height - (picking ? 54 : 26) }}
      />
    </div>
  );
}
